import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { tokenStore } from '@/auth/token-store';
import { Shell } from '@/components/shell';
import { LoginPage } from '@/pages/login';
import { SignupPage } from '@/pages/signup';
import { LoadsPage } from '@/pages/loads';
import { LoadDetailPage } from '@/pages/load-detail';
import { CreateLoadPage } from '@/pages/create-load';
import { TeamMemberDetailPage } from '@/pages/team-member-detail';
import { JoinPage } from '@/pages/join';
import { VehiclesPage } from '@/pages/vehicles';
import { VehicleDetailPage } from '@/pages/vehicle-detail';
import { FleetMapPage } from '@/pages/fleet-map';
import { CustomersPage } from '@/pages/customers';
import { CustomerDetailPage } from '@/pages/customer-detail';
import { LoadBoardPage } from '@/pages/load-board';
import { StatisticsPage } from '@/pages/statistics';
import { InvoicesPage } from '@/pages/invoices';
import { InvoiceDetailPage } from '@/pages/invoice-detail';
import {
  SettingsPage,
  SettingsCompanyPage,
  SettingsRatesPage,
  SettingsBrandPage,
  SettingsDomainsPage,
  SettingsTeamPage,
  SettingsBillingPage,
} from '@/pages/settings';
import { YourWebsitePage } from '@/pages/your-website';
import { SupportPage } from '@/pages/support';

const rootRoute = createRootRoute({
  component: Outlet,
});

// Team unit: the web door for invite codes. Works signed-out (new office
// hires land here from the invite email) and signed-in (redeem into a
// second fleet); JoinPage owns both cases.
const joinSearchSchema = z.object({
  code: z.string().optional(),
});
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join',
  component: JoinPage,
  validateSearch: joinSearchSchema,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    if (tokenStore.isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

// P1: standalone, linkable signup — organic posts point here before the
// marketing site exists.
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  // No auth redirect here: the wizard provisions the fleet mid-flow (steps
  // 4–6 run authenticated), and the Stripe purchase round-trip returns to
  // /signup already authenticated. SignupPage owns the authenticated case —
  // it resumes the wizard at the right step (rates / brand / domain polling)
  // or sends a genuinely-finished user on to '/'. A synchronous bounce here
  // would strip the ?domain= return marker and skip the whole resume.
  component: SignupPage,
});

const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: () => {
    if (!tokenStore.isAuthenticated()) {
      throw redirect({ to: '/login' });
    }
  },
  component: Shell,
});

// Loads list - accepts ?driverUserId= or ?customerId= as scoped filters
// surfaced in the URL. Validated via Zod so unknown params get stripped.
const loadsSearchSchema = z.object({
  driverUserId: z.string().optional(),
  customerId: z.string().optional(),
});

const loadsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/',
  component: LoadsPage,
  validateSearch: loadsSearchSchema,
});

// IMPORTANT: /loads/new is registered BEFORE the dynamic route so
// "new" resolves to this page, not as a loadId param.
const createLoadRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/loads/new',
  component: CreateLoadPage,
});

const loadDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/loads/$loadId',
  component: LoadDetailPage,
});

// Team unit: /drivers folded into /settings/team — redirects keep old
// bookmarks and deep links working (the /settings/website precedent).
const legacyDriversRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/drivers',
  beforeLoad: () => {
    throw redirect({ to: '/settings/team' });
  },
});

const legacyDriverDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/drivers/$memberId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/settings/team/$memberId',
      params: { memberId: params.memberId },
    });
  },
});

const vehiclesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/vehicles',
  component: VehiclesPage,
});

const vehicleDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/vehicles/$vehicleId',
  component: VehicleDetailPage,
});

const customersRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/customers',
  component: CustomersPage,
});

const customerDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/customers/$customerId',
  component: CustomerDetailPage,
});

const loadBoardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/load-postings',
  component: LoadBoardPage,
});

// Money Suite M1 — fleet statistics.
const statisticsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/statistics',
  component: StatisticsPage,
});

// Money Suite M2a — fleet invoicing (a fleet billing ITS customers; this is
// not /settings/billing, which is Breaker's own subscription).
const invoicesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/invoices',
  component: InvoicesPage,
});
const invoiceDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/invoices/$invoiceId',
  component: InvoiceDetailPage,
});

// Website Studio is a creation tool, not a setting — it lives top-level at
// /website (nav beside Loads/Customers/Statistics). The old /settings/website
// redirects so bookmarks and pasted links never 404.
const websiteRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/website',
  component: YourWebsitePage,
});

const legacyWebsiteRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/website',
  beforeLoad: () => {
    throw redirect({ to: '/website' });
  },
});

// P2 billing. ?checkout=success|cancelled comes back from hosted Stripe
// Checkout; anything else is stripped.
const billingSearchSchema = z.object({
  checkout: z.enum(['success', 'cancelled']).optional(),
});

// Settings IA: a hub index at /settings, each area on its own route.
const settingsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});
const settingsCompanyRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/company',
  component: SettingsCompanyPage,
});
const settingsRatesRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/rates',
  component: SettingsRatesPage,
});
const settingsBrandRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/brand',
  component: SettingsBrandPage,
});
const settingsDomainsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/domains',
  component: SettingsDomainsPage,
});
const settingsTeamRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/team',
  component: SettingsTeamPage,
});
// Team unit: the person page — absorbed the old driver detail.
const settingsTeamMemberRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/team/$memberId',
  component: TeamMemberDetailPage,
});
const settingsBillingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/settings/billing',
  component: SettingsBillingPage,
  // ?checkout=success|cancelled comes back from hosted Stripe Checkout (via
  // the /billing redirect or directly); BillingPage reads it.
  validateSearch: billingSearchSchema,
});

// Billing collapsed to one page (ruling 2026-08-15): /settings/billing is
// the home; /billing redirects there, carrying ?checkout so the trial
// banner, Stripe returns, and old links keep working.
const billingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/billing',
  validateSearch: billingSearchSchema,
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/settings/billing', search });
  },
});

const mapRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/map',
  component: FleetMapPage,
});

// The platform-admin section (S69) and the owner support queue moved to
// the admin console (Phase A, law-4 migration) — old /admin* and
// /support/$ticketId deep links now land on the SPA's 404.

// Support v1: fleet users raise + read their own fleet's tickets.
const supportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/support',
  component: SupportPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  joinRoute,
  authedLayoutRoute.addChildren([
    loadsRoute,
    createLoadRoute,
    loadDetailRoute,
    legacyDriversRoute,
    legacyDriverDetailRoute,
    vehiclesRoute,
    vehicleDetailRoute,
    customersRoute,
    customerDetailRoute,
    loadBoardRoute,
    statisticsRoute,
    invoicesRoute,
    invoiceDetailRoute,
    settingsRoute,
    settingsCompanyRoute,
    settingsRatesRoute,
    settingsBrandRoute,
    settingsDomainsRoute,
    settingsTeamRoute,
    settingsTeamMemberRoute,
    settingsBillingRoute,
    websiteRoute,
    legacyWebsiteRoute,
    billingRoute,
    mapRoute,
    supportRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
